import { autoUpdate, ClientRectObject, computePosition, flip, offset, shift } from '@floating-ui/react-dom'
import { TextInlineFormatBar } from 'common/Prose/components/TextInlineFormatBar/TextInlineFormatBar'
import { AllSelection, EditorState, NodeSelection, TextSelection } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { QueryClient } from 'react-query'
import { EDITOR_CONTAINER_ID } from 'screens/ProseEdit/Constants'
import { DocStore, DocStoreContext } from 'stores/DocStore'
import { AlmanacEditorState, AlmanacEditorStateContext } from 'stores/EditorState'
import { ArticleFlat, Layer } from 'types'
import { reportError } from 'utils/error'
import { getInlineFormatBarPluginState } from '../InlineFormatBarPluginKey'
import { elementFromSelection } from './elementFromSelection'
import Styles from './FormatBarView.module.scss'
import { getBrowserSelectionRect, getFakeSelectionRect } from './getBrowserSelectionRect'
import { isSelectionVisible } from './utils'
import { createRoot, Root } from 'react-dom/client'
import { IStackManager } from 'almanac-ui/Modal/Types'
import { ModalStackContext } from 'almanac-ui/Modal/ModalStack'
import { CloudinaryProvider } from 'providers/CloudinaryProvider/CloudinaryProvider'

let caretPos: ClientRectObject | null
let mouseDown = false

export class FormatBarView {
  article: ArticleFlat
  layer: Layer
  view: EditorView
  queryClient: QueryClient
  docStore: DocStore
  inCommentMode?: boolean
  formatBarContainer: HTMLDivElement
  editorStateStore: AlmanacEditorState
  initialized: boolean
  autoUpdateCleanup?: () => void
  root: Root
  stackManager: IStackManager

  constructor(view: EditorView) {
    const { article, layer, queryClient, docStore, inCommentMode, stackManager } = getInlineFormatBarPluginState(
      view.state,
    )
    this.view = view
    this.article = article
    this.layer = layer
    this.queryClient = queryClient
    this.docStore = docStore
    this.inCommentMode = inCommentMode
    this.formatBarContainer = document.createElement('div')
    this.editorStateStore = new AlmanacEditorState(view)
    this.formatBarContainer.classList.add(Styles.wrapper)
    this.initialized = false
    this.root = createRoot(this.formatBarContainer)
    this.stackManager = stackManager

    document.addEventListener('mousedown', this.handleMousedown)
    document.addEventListener('mouseup', this.handleMouseup)
    this.addToDom(view)
    this.update(view)
  }

  addToDom = (view: any) => {
    if (view.dom.parentNode) {
      view.dom.parentNode.appendChild(this.formatBarContainer)
      this.initialized = true
    }
  }

  hideFormatBar = () => {
    Object.assign(this.formatBarContainer.style, {
      display: 'none',
    })
  }

  showFormatBar = () => {
    if (this.formatBarContainer.style.display === 'block') return
    Object.assign(this.formatBarContainer.style, {
      display: 'block',
      opacity: 0,
      visibility: 'hidden',
      pointerEvents: 'none',
    })
    this.updatePosition(this.view, this.formatBarContainer)
    setTimeout(() => {
      Object.assign(this.formatBarContainer.style, {
        opacity: 1,
        visibility: 'visible',
        pointerEvents: 'all',
      })
    }, 80)
  }

  handleMouseup = (e: MouseEvent) => {
    mouseDown = false
    const { formatBarVisible } = getInlineFormatBarPluginState(this.view.state)
    if (formatBarVisible && e.target instanceof Element && e.target.closest(`#${EDITOR_CONTAINER_ID}`)) {
      this.showFormatBar()
    }
    this.update(this.view)
  }

  handleMousedown = (e: MouseEvent) => {
    mouseDown = true
    const { formatBarVisible } = getInlineFormatBarPluginState(this.view.state)
    const clickableElementParents = [
      this.formatBarContainer,
      document.getElementById('floating-ui-root'),
      document.querySelector('.proseDropdownPromptdropdownContainer'),
    ]
    if (this.view.state.selection instanceof AllSelection || this.view.state.selection instanceof NodeSelection) return
    if (e.target instanceof Node && clickableElementParents.some((element) => element?.contains(e.target as Node))) {
      return
    }
    if (formatBarVisible) {
      this.hideFormatBar()
    }
    this.update(this.view)
  }

  update = (view: EditorView, prevState?: EditorState) => {
    if (!this.initialized) this.addToDom(view)
    this.view = view
    this.editorStateStore.updateFromView(view)

    const { formatBarVisible, runItemKey } = getInlineFormatBarPluginState(view.state)
    if (!formatBarVisible) {
      this.hideFormatBar()
      return
    }
    this.updatePosition(view, this.formatBarContainer, prevState)

    if (formatBarVisible && !mouseDown) {
      this.showFormatBar()
    }

    this.root.render(
      <CloudinaryProvider>
        <AlmanacEditorStateContext.Provider value={this.editorStateStore}>
          <DocStoreContext.Provider value={this.docStore}>
            <ModalStackContext.Provider value={this.stackManager}>
              <TextInlineFormatBar
                view={this.view}
                article={this.article}
                layer={this.layer}
                queryClient={this.queryClient}
                inCommentMode={this.inCommentMode}
                docStore={this.docStore}
                runItemKey={runItemKey}
              />
            </ModalStackContext.Provider>
          </DocStoreContext.Provider>
        </AlmanacEditorStateContext.Provider>
      </CloudinaryProvider>,
    )
    // Hide the format bar if the selection is not in view
    if (view.state.selection instanceof TextSelection && !(isSelectionVisible(view) ?? false)) {
      this.hideFormatBar()
    }
  }

  destroy() {
    if (this.autoUpdateCleanup) {
      this.autoUpdateCleanup()
      this.autoUpdateCleanup = undefined
    }
    document.removeEventListener('mousedown', this.handleMousedown)
    document.removeEventListener('mouseup', this.handleMouseup)
    setTimeout(() => this.root.unmount(), 0)
    this.formatBarContainer.remove()
  }

  updatePosition(view: EditorView, wrapper: HTMLElement, prevState?: EditorState) {
    if (prevState && view.state.selection.eq(prevState.selection) && (caretPos?.width || 0) > 0) {
      // if the selection is unchanged, do not need to update position
      return
    }

    if (view.state.selection instanceof TextSelection) {
      let caretData = view.hasFocus() ? getBrowserSelectionRect() : getFakeSelectionRect(view)

      caretPos = caretData ?? caretPos
      if (!caretPos) {
        return
      }

      // Returning above if caretPos is null
      const virtualElement = { getBoundingClientRect: () => caretPos as ClientRectObject }

      const crossAxisOffset = view.editable ? -55 : 0
      const shiftBoundary = document.getElementById(EDITOR_CONTAINER_ID)
      if (!shiftBoundary) reportError('No editor boundary found rendering format bar')

      if (this.autoUpdateCleanup) this.autoUpdateCleanup()
      this.autoUpdateCleanup = autoUpdate(
        virtualElement,
        wrapper,
        () => {
          computePosition(virtualElement, wrapper, {
            placement: 'bottom-start',
            middleware: [
              offset({ crossAxis: crossAxisOffset, mainAxis: 6 }),
              shift({ boundary: shiftBoundary ?? document.body }),
              flip(),
            ],
          }).then(({ x, y }) => {
            const top = Math.max(2, Math.round(y))
            const left = Math.round(x)
            Object.assign(wrapper.style, {
              transform: `translate(${left}px, ${top}px)`,
            })
          })
        },
        { animationFrame: true },
      )
    }

    if (view.state.selection instanceof NodeSelection) {
      const element = elementFromSelection(view)
      if (!element) return
      const offsetAmount = 5

      if (this.autoUpdateCleanup) this.autoUpdateCleanup()
      this.autoUpdateCleanup = autoUpdate(element, wrapper, () => {
        computePosition(element, wrapper, {
          placement: 'top',
          middleware: [offset(offsetAmount)],
        }).then(({ x, y }) => {
          const top = Math.round(y)
          const left = Math.round(x)
          Object.assign(wrapper.style, {
            transform: `translate(${left}px, ${top}px)`,
          })
        })
      })
    }
    if (view.state.selection instanceof AllSelection) {
      const element = elementFromSelection(view)
      if (!element) return

      if (this.autoUpdateCleanup) this.autoUpdateCleanup()
      this.autoUpdateCleanup = autoUpdate(element, wrapper, () => {
        computePosition(element, wrapper, {
          placement: 'top',
        }).then(({ x, y }) => {
          const top = Math.max(2, Math.round(y))
          const left = Math.round(x)
          Object.assign(wrapper.style, {
            transform: `translate(${left}px, ${top}px)`,
          })
        })
      })
    }
  }
}
